/**
 * `citationDisplayNames.ts` — relabel a document citation with the name an
 * author set in the Define app, on the way to the user.
 *
 * `AIFileCitation.fileName` arrives from OpenAI's `file_search` annotation and
 * is the UPLOADED FILE's name (`Robodog_Manual 1.pdf`). Inside the knowledge
 * transport that is IDENTITY — `findPdfAsync` matches a requested PDF against
 * it and `withSources` de-duplicates on it — so the rename happens here, at
 * the edge, after every match is settled.
 *
 * ⚠ Nothing is REWRITTEN here any more. `resolveDocumentName`'s answer is
 * ANNOTATED onto `displayName` and `fileName` is left as it arrived, because
 * `fileId`, `storagePath` and `fileName` are all identity: the first two are
 * what the chip's click handler opens and what the source list de-duplicates
 * on, and the third is what `findPdfAsync` matches a requested PDF against. A
 * display name is not unique and two documents may legitimately share one.
 * `toChipCitations` is where the label finally displaces the file name, at the
 * last possible moment, for the chip alone.
 *
 * ⚠ **The `Sources (n)` line now carries BOTH names, and that is the
 * 2026-09-04 direction call honoured rather than reversed** (changed
 * 2026-09-21). That call asked for the title in the doc reference and kept the
 * raw name in the model's own input, on the reasoning that a title is
 * frequently LESS specific than its filename and the filename is the word the
 * agent uses to notice it is holding the wrong document. Both still hold — so
 * the title is ADDED to that line, never substituted, by
 * `agentFacingDocumentName`. What the old split actually cost: asked "do you
 * have a user manual?", Spark answered `I found a user manual titled
 * "Robodog_Manual 1.pdf"` above a chip reading `Robodog Manual`, because the
 * title had never reached it. Do NOT close the loop the other way by feeding
 * `toChipCitations` output into the agent text — that drops the identity word
 * this whole split exists to protect.
 *
 * ⚠ Known cost, recorded rather than hidden: the Answer skill routes the raw
 * names into `present_options` buttons, so a user choosing between documents
 * can see file names on the buttons and titles on the chips. If that is
 * reported, the fix is the BUTTON labels, not this file.
 *
 * **Driven live 2026-09-22**, `gpt-realtime-2.1`, RoboDog fixture: "Do you have
 * a user manual?" → *"Yes. There is a user manual available called Robodog
 * Manual."* The title is spoken, the file name is not, and the `(file: …)`
 * bracket is not read aloud. The regression it replaces answered `I found a
 * user manual titled "Robodog_Manual 1.pdf"`. The tool-result half is separately
 * verified through the real `searchDocsByFileNameAsync` and negative-controlled
 * four ways; re-drive with that same question, since which NAME is spoken is a
 * prompt rule (`skills/answer.md`, Global response rules) and a prompt rule
 * shipping is never evidence it is obeyed.
 */

import type { AIFileCitation } from "@ai/context/AIContextEngineModel.KnowledgeTransport";
import { resolveDocumentName } from "@models/docs/documentDisplayNames";
import type { AgentToolExecutorHost } from "./AgentToolExecutorModel";

/**
 * Annotate each citation with its Define-app title, leaving every identity
 * field alone. A citation that already carries one — a manifest hit, which is
 * built straight from `DocLibraryItem.displayName` — is passed through
 * untouched rather than re-resolved.
 */
export async function withDocumentDisplayNames(
  host: AgentToolExecutorHost,
  citations: ReadonlyArray<AIFileCitation> | null,
): Promise<ReadonlyArray<AIFileCitation> | null> {
  if (!citations?.length) return citations;
  if (citations.every((c) => c.displayName)) return citations;
  if (!host.loadDocumentNameIndexAsync) {
    // A host built before this seam existed - in dev, a live session whose
    // orchestrator predates a hot reload. Distinguishable from "never ran".
    if (import.meta.env.DEV) console.warn("[citationDisplayNames] host has no loadDocumentNameIndexAsync");
    return citations;
  }

  const index = await host.loadDocumentNameIndexAsync();
  if (index.size === 0) {
    if (import.meta.env.DEV) console.warn("[citationDisplayNames] document name index is EMPTY");
    return citations;
  }

  return citations.map((citation) => {
    if (citation.displayName) return citation;
    const display = resolveDocumentName(index, citation.storagePath, citation.fileName);
    if (import.meta.env.DEV && !display) {
      // Why a chip still shows its file name. The three causes look identical
      // on screen: the library never loaded, the keys do not line up, or the
      // Define row carries no `documentName`, so print both sides.
      console.warn(
        "[citationDisplayNames] unresolved",
        { storagePath: citation.storagePath, fileName: citation.fileName },
        "index:", [...index.entries()].slice(0, 20),
      );
    }
    return display && display !== citation.fileName
      ? { ...citation, displayName: display }
      : citation;
  });
}

/**
 * Collapse the annotation into the one name a CHIP shows. The chip is terminal
 * — nothing downstream of it matches on the label — so this is the one place a
 * title may stand in for a file name, and it happens at the very last step.
 *
 * `fileId` and `storagePath` are carried through untouched: they are what the
 * click handler opens and what `publishCitations` de-duplicates on.
 */
export function toChipCitations(
  citations: ReadonlyArray<AIFileCitation>,
): ReadonlyArray<AIFileCitation> {
  return citations.map((citation) =>
    citation.displayName && citation.displayName !== citation.fileName
      ? { ...citation, fileName: citation.displayName }
      : citation,
  );
}
